import type { GlobalDetectionPresetId } from './detectionPresets';
import { normalizeDetectionLevelId } from './detectionPresets';

/** 통합 관제 AI 감지 — 시스템 설정 기본값(전역) */

/**
 * 필터 단계 복구(권장 순서):
 * 1) POOL_MONITOR_RAW_SNAPSHOTS=0, TRUST_SERVER_SNAPSHOT_OVERLAY=false
 * 2) POOL_YOLO_OVERHEAD_BASELINE=1
 * 3) USE_HYBRID_MONITOR_PIPELINE=true, DECK_STUCK env
 */
/** 백엔드 무필터 RAW — 디버그 전용 */
export const DRAW_RAW_SERVER_SNAPSHOTS = false;

/** true면 서버 bbox를 프론트 필터 없이 표시(POOL_MONITOR_RAW_SNAPSHOTS=1 과 함께) */
export const TRUST_SERVER_SNAPSHOT_OVERLAY = false;

/** 서버 central-ai 스냅샷 + 백업식 프론트 필터 + (필요 시) /api/yolo/detect 보조 */
export const USE_HYBRID_MONITOR_PIPELINE = true;

/** 서버 스냅샷이 비었을 때만 /api/yolo/detect 보조.
 * 중앙 AI가 돌 때 브라우저가 480 반해상도로 detect 하면 박스가 흔들림 — 기본 OFF.
 */
export const HYBRID_CLIENT_YOLO_FALLBACK = false;

export interface MonitoringAiSettings {
  /** 시스템 관리: 중앙 관제 서버 AI 루프 전역 ON/OFF */
  server_ai_enabled: boolean;
  /** legacy(opt-in off): 추론 제외 시설 ID */
  excluded_pool_ids: number[];
  /** opt-in on: 감시 스위치 ON인 정상 운영 시설 ID (기본 []) */
  included_pool_ids: number[];
  /** true: 토글 ON인 시설만 감시(기본). false: legacy(제외 목록만 빼고 전부 감시) */
  central_monitor_opt_in: boolean;
  /** manual: 시설 내 CCTV 대수로 구간 / auto: 전체 AI CCTV·GPU 부하로 자동 */
  capture_width_mode: 'manual' | 'auto';
  capture_max_width_1: number;
  capture_max_width_2_3: number;
  capture_max_width_4_plus: number;
  jpeg_quality: number;
  interval_base_ms: number;
  interval_per_cam_ms: number;
  interval_cap_ms: number;
  max_active_detect_cams: number;
  min_confidence: number;
  min_box_area_ratio: number;
  min_box_side_px: number;
  evaluate_motion_risk: boolean;
  /** OpenPose ENABLED — 서버 재시작 없이 DB 저장·런타임 반영 */
  openpose_enabled: boolean;
  /** ENABLED일 때만 의미: 트리거 조건 충족 시에만 OpenPose (기본 true) */
  openpose_trigger_only: boolean;
  /** 관제 화면 스켈레톤 오버레이 표시 */
  openpose_show_skeleton: boolean;
  /** true: openpose_ran 일 때만 스켈레톤 (트리거 미발동·스티키 제외) */
  openpose_show_skeleton_trigger_only: boolean;
  track_iou_threshold: number;
  max_track_misses: number;
  min_track_hits: number;
  gpu_allocation_enabled: boolean;
  gpu_auto_assign: boolean;
  gpu_cctv_overrides: Record<string, number>;
  /** UI: 검출 성향 1~8단계 (저장 시 DB monitoring_ai.detection_preset) */
  detection_preset?: GlobalDetectionPresetId;
  /** models/ 파일명 — 전역 기본 YOLO (시설 ai_detector_model 우선) */
  default_yolo_model: string;
  /** dedicated | hybrid_recall | coco_only_debug | raw_person_debug */
  yolo_pipeline_preset:
    | 'dedicated'
    | 'hybrid_recall'
    | 'coco_only_debug'
    | 'raw_person_debug';
  /**
   * 목표 추론 FPS (0=기존 간격만 사용).
   * 기본 3 — 부하·반응 균형. >0이면 라운드 주기를 max(기존간격, 1000/fps)로 늦춤.
   */
  infer_target_fps: number;
  /**
   * YOLO imgsz 오버라이드 (0=자동·코드 기본).
   * 기본 640 — 경량. 탑뷰(overhead)에는 적용하지 않음.
   */
  yolo_imgsz: number;
  /** CUDA Ultralytics FP16 (기본 true — 속도 우선, 점수 미세 차이 가능) */
  yolo_fp16: boolean;
  /**
   * YOLO TensorRT .engine 사용 (기본 false).
   * models/에 선택 모델과 같은 이름의 .engine 이 있으면 사용, 없으면 .pt 폴백.
   */
  yolo_tensorrt: boolean;
  /** CCTV 용도별 infer tier (A=수면·물놀이 매 라운드, B=데크, C=통로 등) */
  infer_tier_enabled: boolean;
  /** max_active_detect_cams 초과 시 ID 순 prefix 대신 라운드로빈 */
  infer_rotate_all_cams: boolean;
  infer_tier_b_every_n_rounds: number;
  infer_tier_c_every_n_rounds: number;
  /** L2(motion risk) OFF면 risk_alerts 비움 */
  alert_requires_l2: boolean;
  /** 팝업·발송 최소 등급 */
  alert_min_risk_level: '주의' | '위험';
  /** 웨어러블 융합: off | hint | gate_pool_drown */
  wearable_alert_fusion: 'off' | 'hint' | 'gate_pool_drown';
}

export const DEFAULT_MONITORING_AI_SETTINGS: MonitoringAiSettings = {
  server_ai_enabled: true,
  excluded_pool_ids: [],
  included_pool_ids: [],
  central_monitor_opt_in: true,
  capture_width_mode: 'manual',
  /** 720p HLS = 1280×720 — 캡처 폭 720은 세로 405로 깎이므로 가로 1280 유지 */
  capture_max_width_1: 1280,
  capture_max_width_2_3: 960,
  capture_max_width_4_plus: 720,
  jpeg_quality: 0.8,
  interval_base_ms: 320,
  interval_per_cam_ms: 140,
  interval_cap_ms: 900,
  max_active_detect_cams: 18,
  min_confidence: 0.30,
  min_box_area_ratio: 0.0006,
  min_box_side_px: 10,
  evaluate_motion_risk: true,
  openpose_enabled: false,
  openpose_trigger_only: true,
  openpose_show_skeleton: true,
  openpose_show_skeleton_trigger_only: true,
  track_iou_threshold: 0.04,
  max_track_misses: 12,
  min_track_hits: 3,
  gpu_allocation_enabled: true,
  gpu_auto_assign: true,
  gpu_cctv_overrides: {},
  detection_preset: '5',
  default_yolo_model: 'detector_ver5.pt',
  yolo_pipeline_preset: 'dedicated',
  infer_target_fps: 3,
  yolo_imgsz: 640,
  yolo_fp16: true,
  yolo_tensorrt: false,
  infer_tier_enabled: true,
  infer_rotate_all_cams: true,
  infer_tier_b_every_n_rounds: 2,
  infer_tier_c_every_n_rounds: 3,
  alert_requires_l2: true,
  alert_min_risk_level: '주의',
  wearable_alert_fusion: 'hint',
};

export function mergeMonitoringAiSettings(
  partial?: Partial<MonitoringAiSettings> | null
): MonitoringAiSettings {
  const p = partial || {};
  const d = DEFAULT_MONITORING_AI_SETTINGS;
  const num = (v: unknown, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const bool = (v: unknown, fallback: boolean) => {
    if (typeof v === 'boolean') return v;
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0') return false;
    return fallback;
  };
  const excludedRaw = p.excluded_pool_ids;
  let excluded_pool_ids: number[] = d.excluded_pool_ids;
  if (Array.isArray(excludedRaw)) {
    excluded_pool_ids = excludedRaw
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n));
  }
  const includedRaw = p.included_pool_ids;
  let included_pool_ids: number[] = d.included_pool_ids;
  if (Array.isArray(includedRaw)) {
    included_pool_ids = includedRaw
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n));
  }

  return {
    server_ai_enabled: bool(p.server_ai_enabled, d.server_ai_enabled),
    excluded_pool_ids,
    included_pool_ids,
    central_monitor_opt_in: bool(p.central_monitor_opt_in, d.central_monitor_opt_in),
    capture_width_mode:
      String(p.capture_width_mode || d.capture_width_mode).toLowerCase() === 'auto'
        ? 'auto'
        : 'manual',
    capture_max_width_1: Math.max(320, num(p.capture_max_width_1, d.capture_max_width_1)),
    capture_max_width_2_3: Math.max(320, num(p.capture_max_width_2_3, d.capture_max_width_2_3)),
    capture_max_width_4_plus: Math.max(320, num(p.capture_max_width_4_plus, d.capture_max_width_4_plus)),
    jpeg_quality: Math.min(1, Math.max(0.1, num(p.jpeg_quality, d.jpeg_quality))),
    interval_base_ms: Math.max(100, num(p.interval_base_ms, d.interval_base_ms)),
    interval_per_cam_ms: Math.max(0, num(p.interval_per_cam_ms, d.interval_per_cam_ms)),
    interval_cap_ms: Math.max(200, num(p.interval_cap_ms, d.interval_cap_ms)),
    max_active_detect_cams: Math.max(0, Math.floor(num(p.max_active_detect_cams, d.max_active_detect_cams))),
    min_confidence: Math.min(1, Math.max(0.01, num(p.min_confidence, d.min_confidence))),
    min_box_area_ratio: Math.min(1, Math.max(0, num(p.min_box_area_ratio, d.min_box_area_ratio))),
    min_box_side_px: Math.max(1, num(p.min_box_side_px, d.min_box_side_px)),
    evaluate_motion_risk: bool(p.evaluate_motion_risk, d.evaluate_motion_risk),
    openpose_enabled: bool(p.openpose_enabled, d.openpose_enabled),
    openpose_trigger_only: bool(p.openpose_trigger_only, d.openpose_trigger_only),
    openpose_show_skeleton: bool(p.openpose_show_skeleton, d.openpose_show_skeleton),
    openpose_show_skeleton_trigger_only: bool(
      p.openpose_show_skeleton_trigger_only,
      d.openpose_show_skeleton_trigger_only
    ),
    track_iou_threshold: Math.min(1, Math.max(0, num(p.track_iou_threshold, d.track_iou_threshold))),
    max_track_misses: Math.max(1, num(p.max_track_misses, d.max_track_misses)),
    min_track_hits: Math.max(1, num(p.min_track_hits, d.min_track_hits)),
    gpu_allocation_enabled: bool(p.gpu_allocation_enabled, d.gpu_allocation_enabled),
    gpu_auto_assign: bool(p.gpu_auto_assign, d.gpu_auto_assign),
    gpu_cctv_overrides: (() => {
      const raw = p.gpu_cctv_overrides;
      if (!raw || typeof raw !== 'object') return { ...d.gpu_cctv_overrides };
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(raw)) {
        const n = Number(v);
        if (Number.isFinite(n)) out[String(k)] = n;
      }
      return out;
    })(),
    detection_preset: (() => {
      const raw = p.detection_preset;
      if (raw === 'custom') return 'custom';
      const level = normalizeDetectionLevelId(raw);
      if (level) return level;
      return d.detection_preset ?? '5';
    })(),
    default_yolo_model: String(p.default_yolo_model || d.default_yolo_model).trim() || d.default_yolo_model,
    yolo_pipeline_preset: (() => {
      const raw = String(p.yolo_pipeline_preset || d.yolo_pipeline_preset).toLowerCase();
      if (raw === 'hybrid_recall' || raw === 'hybrid' || raw === 'ver5_hybrid') return 'hybrid_recall';
      if (raw === 'coco_only_debug' || raw === 'coco_only' || raw === 'coco') return 'coco_only_debug';
      if (
        raw === 'raw_person_debug' ||
        raw === 'raw_person' ||
        raw === 'raw' ||
        raw === 'raw_yolo' ||
        raw === 'yolo_raw'
      ) {
        return 'raw_person_debug';
      }
      return 'dedicated';
    })(),
    infer_target_fps: (() => {
      const n = num(p.infer_target_fps, d.infer_target_fps);
      if (n <= 0) return 0;
      return Math.min(30, Math.max(0.5, n));
    })(),
    yolo_imgsz: (() => {
      const n = Math.floor(num(p.yolo_imgsz, d.yolo_imgsz));
      if (n <= 0) return 0;
      return Math.min(1920, Math.max(320, n));
    })(),
    yolo_fp16: bool(p.yolo_fp16, d.yolo_fp16),
    yolo_tensorrt: bool(p.yolo_tensorrt, d.yolo_tensorrt),
    infer_tier_enabled: bool(p.infer_tier_enabled, d.infer_tier_enabled),
    infer_rotate_all_cams: bool(p.infer_rotate_all_cams, d.infer_rotate_all_cams),
    infer_tier_b_every_n_rounds: Math.max(
      1,
      Math.min(12, Math.floor(num(p.infer_tier_b_every_n_rounds, d.infer_tier_b_every_n_rounds)))
    ),
    infer_tier_c_every_n_rounds: Math.max(
      1,
      Math.min(12, Math.floor(num(p.infer_tier_c_every_n_rounds, d.infer_tier_c_every_n_rounds)))
    ),
    alert_requires_l2: bool(p.alert_requires_l2, d.alert_requires_l2),
    alert_min_risk_level: (() => {
      const raw = String(p.alert_min_risk_level || d.alert_min_risk_level).trim();
      if (raw === '위험' || raw === 'high' || raw === 'danger') return '위험';
      return '주의';
    })(),
    wearable_alert_fusion: (() => {
      const raw = String(p.wearable_alert_fusion || d.wearable_alert_fusion).trim().toLowerCase();
      if (raw === 'off' || raw === 'none') return 'off';
      if (raw === 'gate_pool_drown' || raw === 'gate') return 'gate_pool_drown';
      return 'hint';
    })(),
  };
}

export function getCaptureMaxWidth(cctvCount: number, s: MonitoringAiSettings): number {
  const n = Math.max(1, cctvCount);
  if (n <= 1) return s.capture_max_width_1;
  if (n <= 3) return s.capture_max_width_2_3;
  return s.capture_max_width_4_plus;
}

function autoScaleCaptureWidth(
  capPx: number,
  globalActiveCamCount: number,
  gpuCount: number
): number {
  const globalN = Math.max(1, globalActiveCamCount);
  if (globalN <= 1) return capPx;
  const over = globalN - 1;
  let penalty = 0.035 * over;
  if (gpuCount >= 2) penalty *= 0.82;
  const factor = Math.max(0.45, 1 - penalty);
  let w = Math.round(capPx * factor);
  // 960 스트림을 480으로 반토막 내지 않음 (이미 받은 프레임 품질 유지)
  w = Math.max(720, Math.min(capPx, Math.floor(w / 16) * 16));
  return w;
}

/** 서버 central-ai·브라우저 보조 detect 공통 추론 가로(px) */
export function resolveInferCaptureMaxWidth(
  s: MonitoringAiSettings,
  opts: {
    cctvCountInPool: number;
    globalActiveCamCount: number;
    gpuCount?: number;
  }
): number {
  const poolN = Math.max(1, opts.cctvCountInPool);
  const globalN = Math.max(1, opts.globalActiveCamCount);
  const tierN = s.capture_width_mode === 'auto' ? globalN : poolN;
  let cap = getCaptureMaxWidth(tierN, s);
  if (s.capture_width_mode === 'auto') {
    cap = autoScaleCaptureWidth(cap, globalN, Math.max(1, opts.gpuCount ?? 1));
  }
  return cap;
}

export function getDetectionCooldownMs(cctvCount: number, s: MonitoringAiSettings): number {
  const n = Math.max(0, cctvCount - 1);
  return Math.min(s.interval_cap_ms, s.interval_base_ms + n * s.interval_per_cam_ms);
}

/** 서버·클라이언트 공통 박스/신뢰도 필터 (전역 monitoring_ai, 풀/CCTV conf는 서버에서 선적용) */
export function passesDetectionDisplayFilters(
  bbox: number[],
  conf: number,
  frameW: number,
  frameH: number,
  s: MonitoringAiSettings,
  hits = 1
): boolean {
  if (!Array.isArray(bbox) || bbox.length !== 4) return false;
  if (conf < s.min_confidence) return false;
  if (s.min_track_hits > 1 && hits >= 1 && hits < s.min_track_hits) return false;
  const fw = Math.max(1, frameW);
  const fh = Math.max(1, frameH);
  const x1 = Number(bbox[0]);
  const y1 = Number(bbox[1]);
  const x2 = Number(bbox[2]);
  const y2 = Number(bbox[3]);
  const w = Math.max(0, x2 - x1);
  const h = Math.max(0, y2 - y1);
  if (w < s.min_box_side_px || h < s.min_box_side_px) return false;
  const areaRatio = (w * h) / (fw * fh);
  if (areaRatio < s.min_box_area_ratio) return false;
  return true;
}

export function maxServerMissForDisplay(s: MonitoringAiSettings): number {
  return Math.max(1, Math.floor(s.max_track_misses));
}

/** 통합 관제 — 이 시설이 AI 감시 대상인지 (opt-in 토글 반영) */
export function isCentralPoolMonitored(
  poolId: number,
  settings: MonitoringAiSettings,
  operatingPoolIds: number[]
): boolean {
  const pid = Number(poolId);
  if (!Number.isFinite(pid)) return false;
  const operating = new Set(
    operatingPoolIds.map((id) => Number(id)).filter((n) => Number.isFinite(n))
  );
  if (!operating.has(pid)) return false;
  const optIn = settings.central_monitor_opt_in !== false;
  if (!optIn) {
    const stored = settings.excluded_pool_ids.map((id) => Number(id));
    return !stored.includes(pid);
  }
  const included = settings.included_pool_ids.map((id) => Number(id));
  return included.includes(pid);
}

/** bbox·서버 스냅샷 오버레이 — 로컬 included와 어긋나도 서버가 보낸 스냅샷이 있으면 표시 */
export function isCentralPoolAiOverlayEligible(
  poolId: number,
  settings: MonitoringAiSettings,
  operatingPoolIds: number[],
  hasServerSnapshotForPool: boolean
): boolean {
  if (hasServerSnapshotForPool) return true;
  return isCentralPoolMonitored(poolId, settings, operatingPoolIds);
}

export function effectiveCentralExcludedPoolIds(
  settings: MonitoringAiSettings,
  operatingPoolIds: number[]
): number[] {
  const operating = operatingPoolIds.map((id) => Number(id)).filter((n) => Number.isFinite(n));
  const optIn = settings.central_monitor_opt_in !== false;
  if (!optIn) {
    return settings.excluded_pool_ids.map((id) => Number(id));
  }
  const included = new Set(settings.included_pool_ids.map((id) => Number(id)));
  return operating.filter((id) => !included.has(id));
}

const ALERT_LEVEL_RANK: Record<string, number> = {
  주의: 1,
  medium: 1,
  위험: 2,
  high: 2,
  danger: 2,
};

export function alertRiskLevelRank(level: unknown): number {
  const s = String(level || '').trim();
  return ALERT_LEVEL_RANK[s] ?? 0;
}

export function minAlertRiskLevelRank(settings: MonitoringAiSettings): number {
  return settings.alert_min_risk_level === '위험' ? 2 : 1;
}

/** 관제 팝업·발송용 risk_alerts 게이트 (서버 필터와 동일 규칙, FE 방어) */
export function passesAlertPopupGate(
  alert: { risk_level?: string; risk_type?: string; source?: string },
  settings: MonitoringAiSettings
): boolean {
  if (settings.alert_requires_l2 && !settings.evaluate_motion_risk) return false;
  if (alertRiskLevelRank(alert.risk_level) < minAlertRiskLevelRank(settings)) return false;
  const fusion = settings.wearable_alert_fusion;
  const riskType = String(alert.risk_type || '').trim().toLowerCase();
  const poolDrown = new Set(['drowning', 'no_motion', 'sinking']);
  if (fusion === 'gate_pool_drown' && poolDrown.has(riskType)) {
    if (alertRiskLevelRank(alert.risk_level) < 2 && String(alert.source || '') !== 'openpose') {
      return false;
    }
  }
  return true;
}
